import React, { useEffect, useState } from "react";

import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { createPortal } from "react-dom";
import {
  Control,
  UseFormSetValue,
  useFieldArray,
  useForm,
} from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { ClauseGroupRow } from "src/clauseBlock/ClauseGroupRow";
import { ClauseDragOverlay } from "src/clauseBlock/ClauseRow";
import {
  BranchClauses,
  getNewClauseGroup,
  toGroups,
} from "src/clauseBlock/conditionGroups";
import { ClauseBlockSize } from "src/clauseBlock/JunctionDropdown";
import { Clause, ClauseGroup, Junctions } from "src/clients/flow-api";
import { Button } from "src/design-system/Button";

type SharedProps = {
  junction: Junctions;
  immutable: boolean;
  size?: ClauseBlockSize;
  fieldPlaceholder?: string;
  valuePlaceholder?: string;
  allowDeletingAllClauses?: boolean;
  fieldErrors?: FieldErrors;
  hideActionButtons?: boolean;
  lazyInputs?: boolean;
};

type Props = SharedProps & {
  clauses: BranchClauses;
  allowAddingGroups: boolean;
  onChange?: (value: GroupableClauseForm) => void;
};

export type ClauseForm = {
  junction: Junctions;
  clauses: Clause[];
};

export type GroupableClauseForm = {
  junction: Junctions;
  clauses: ClauseGroup[];
};

// The form always works on groups: flat branches are wrapped into a lone
// group on the way in (toGroups).
export type ClauseFormValues = {
  junction: Junctions;
  groups: ClauseGroup[];
};

// Attached to each group's droppable body so drops over empty space append.
export type GroupDropData = {
  groupIndex: number;
};

type ClausePosition = {
  groupIndex: number;
  clauseIndex: number;
};

// A cross-group drag in flight: the clause still lives at `from` in the form,
// but the rows render as if it had already moved to `to`.
export type ClauseDragPreview = {
  clause: Clause;
  from: ClausePosition;
  to: ClausePosition;
};

// Rows are sortable by the clause's own id_left, which survives the field
// array regenerating its ids on every cross-group move.
const locateClause = (
  groups: ClauseGroup[],
  id: UniqueIdentifier,
): ClausePosition | null =>
  groups
    .flatMap((group, groupIndex) =>
      group.clauses.map((clause, clauseIndex) => ({
        clauseIndex,
        groupIndex,
        id: clause.id_left,
      })),
    )
    .find((position) => position.id === id) ?? null;

// Returns groups with the clause moved into another group; a source group
// drained empty by the move is dropped.
const moveClauseAcrossGroups = (
  groups: ClauseGroup[],
  from: ClausePosition,
  to: ClausePosition,
): ClauseGroup[] => {
  const moved = groups[from.groupIndex].clauses[from.clauseIndex];
  return groups
    .map((group, groupIndex) => {
      if (groupIndex === from.groupIndex) {
        return {
          ...group,
          clauses: group.clauses.toSpliced(from.clauseIndex, 1),
        };
      }
      if (groupIndex === to.groupIndex) {
        // toSpliced clamps, so an index past the end appends
        return {
          ...group,
          clauses: group.clauses.toSpliced(to.clauseIndex, 0, moved),
        };
      }
      return group;
    })
    .filter(
      (group, groupIndex) =>
        groupIndex !== from.groupIndex || group.clauses.length > 0,
    );
};

// Where the dragged clause would land
const resolveDropTarget = (
  groups: ClauseGroup[],
  { active, over }: DragOverEvent | DragEndEvent,
  dropPreview: ClausePosition | null,
): ClausePosition | null => {
  if (!over) {
    return null;
  }
  if (over.id === active.id) {
    return dropPreview;
  }
  const rowTarget = locateClause(groups, over.id);
  if (rowTarget) {
    const isBelowOverItem =
      active.rect.current.translated !== null &&
      active.rect.current.translated.top > over.rect.top + over.rect.height;
    return {
      ...rowTarget,
      clauseIndex: rowTarget.clauseIndex + (isBelowOverItem ? 1 : 0),
    };
  }
  const groupIndex = (over.data.current as GroupDropData | undefined)
    ?.groupIndex;
  if (groupIndex === undefined) {
    return null;
  }
  return { clauseIndex: groups[groupIndex].clauses.length, groupIndex };
};

// Props threaded unchanged into every row of the block.
export type SharedRowProps = {
  control: Control<ClauseFormValues>;
  setValue: UseFormSetValue<ClauseFormValues>;
  immutable: boolean;
  lazyInputs: boolean;
  allowReordering: boolean;
  size: ClauseBlockSize;
  fieldPlaceholder: string;
  valuePlaceholder: string;
  allowDeletingAllClauses?: boolean;
};

export const GroupableClauseBlock: React.FC<Props> = ({
  clauses,
  junction,
  immutable,
  allowAddingGroups,
  size = "md",
  fieldPlaceholder = "data.field",
  valuePlaceholder = '"comparison value"',
  allowDeletingAllClauses,
  onChange,
  fieldErrors,
  hideActionButtons,
  lazyInputs = false,
}) => {
  const { control, setValue, watch, getValues } = useForm<ClauseFormValues>({
    defaultValues: { junction, groups: toGroups(clauses, junction) },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "groups",
  });
  const [activeDrag, setActiveDrag] = useState<{
    clause: Clause;
    from: ClausePosition;
  } | null>(null);
  const [dropPreview, setDropPreview] = useState<ClausePosition | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    const activator = event.activatorEvent.target;
    if (activator instanceof Element) {
      activator.closest<HTMLElement>("button")?.focus();
    }
    const groups = getValues().groups;
    const from = locateClause(groups, event.active.id);
    setActiveDrag(
      from
        ? { clause: groups[from.groupIndex].clauses[from.clauseIndex], from }
        : null,
    );
  };

  // Previews where the clause would land without touching the form: the rows
  // render as if it had already moved, but the values only change on drop.
  const handleDragOver = (event: DragOverEvent) => {
    const groups = getValues().groups;
    const from = locateClause(groups, event.active.id);
    const to = resolveDropTarget(groups, event, dropPreview);
    // Preview not needed when we are still in the same group as Dnd Sortable will handle that.
    // ---> we can set next to null in that case.
    const next = from && to && from.groupIndex !== to.groupIndex ? to : null;
    // Keep the current object when the position is unchanged so no re-render is triggered.
    setDropPreview((current) =>
      current &&
      next &&
      current.groupIndex === next.groupIndex &&
      current.clauseIndex === next.clauseIndex
        ? current
        : next,
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const groups = getValues().groups;
    const from = locateClause(groups, event.active.id);
    const to = resolveDropTarget(groups, event, dropPreview);
    if (from && to) {
      if (from.groupIndex !== to.groupIndex) {
        setValue("groups", moveClauseAcrossGroups(groups, from, to));
      } else {
        const rowTarget = event.over
          ? locateClause(groups, event.over.id)
          : null;
        const targetIndex = rowTarget
          ? rowTarget.clauseIndex
          : groups[from.groupIndex].clauses.length - 1;
        if (targetIndex !== from.clauseIndex) {
          setValue(
            `groups.${from.groupIndex}.clauses`,
            arrayMove(
              groups[from.groupIndex].clauses,
              from.clauseIndex,
              targetIndex,
            ),
          );
        }
      }
    }
    setActiveDrag(null);
    setDropPreview(null);
  };

  const handleDragCancel = () => {
    setActiveDrag(null);
    setDropPreview(null);
  };

  const handleAddGroup = () => {
    // When we add a 2nd group, we set the junction to default AND
    if (getValues().groups.length === 1) {
      setValue("junction", Junctions.AND);
    }
    append(getNewClauseGroup());
  };

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      const { junction, groups } = values as ClauseFormValues;
      onChange?.({ clauses: groups, junction });
    });

    return () => unsubscribe();
  }, [onChange, watch]);

  const dragPreview: ClauseDragPreview | null =
    activeDrag && dropPreview ? { ...activeDrag, to: dropPreview } : null;

  const sharedRowProps: SharedRowProps = {
    allowDeletingAllClauses,
    allowReordering: allowAddingGroups,
    control,
    fieldPlaceholder,
    immutable,
    lazyInputs,
    setValue,
    size,
    valuePlaceholder,
  };

  return (
    <>
      <DndContext
        collisionDetection={closestCorners}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
      >
        {fields.map((group, index) => (
          <ClauseGroupRow
            key={group.id}
            {...sharedRowProps}
            dragPreview={dragPreview}
            fieldErrors={fieldErrors}
            hideActionButtons={hideActionButtons}
            index={index}
            isLoneGroup={fields.length === 1}
            onDelete={() => remove(index)}
          />
        ))}
        {createPortal(
          <DragOverlay>
            {activeDrag && <ClauseDragOverlay clause={activeDrag.clause} />}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
      {!hideActionButtons && allowAddingGroups && (
        <div className="mt-2">
          <Button
            dataLoc="add-condition-group"
            isDisabled={immutable}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={handleAddGroup}
          >
            Add condition group
          </Button>
        </div>
      )}
    </>
  );
};

export const ClauseBlock: React.FC<
  SharedProps & {
    clauses: Clause[];
    onChange?: (value: ClauseForm) => void;
  }
> = ({ onChange, ...props }) => (
  <GroupableClauseBlock
    {...props}
    allowAddingGroups={false}
    onChange={({ clauses }) =>
      onChange?.({ clauses: clauses[0].clauses, junction: clauses[0].junction })
    }
  />
);
